import { useState } from 'react';
import { X, ChevronRight, Loader2, Film } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { IAvatarVoicePanelProps } from '@/@types/sceneEditorV2';

function AvatarVoicePanel({
  sceneIndex,
  avatarName,
  avatarImageUrl,
  voiceName,
  onSelectAvatar,
  onSelectVoice,
  onRenderScene,
  isRendering,
  canRender,
  onClose,
  voices,
  libraryAvatars,
  selectedLibraryImageId,
}: IAvatarVoicePanelProps) {
  const [avatarPopoverOpen, setAvatarPopoverOpen] = useState(false);
  const [voicePopoverOpen, setVoicePopoverOpen] = useState(false);

  return (
    <div className="flex h-full flex-col p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-base font-medium">
          Avatar & Voice{' '}
          <span className="text-primary light:text-primary-foreground">
            (Scene {sceneIndex + 1})
          </span>
        </span>
        <button
          onClick={onClose}
          className="rounded-md p-0.5 text-foreground-muted transition-colors hover:text-foreground"
        >
          <X size={18} />
        </button>
      </div>

      {/* Avatar selector row */}
      <div className="mb-4">
        <div className="mb-1.5 text-sm text-foreground-muted">Avatar</div>
        <Popover open={avatarPopoverOpen} onOpenChange={setAvatarPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg border border-border',
                'bg-background-secondary px-3 py-2.5 transition-colors',
                'hover:border-border-hover hover:bg-background-secondary/80'
              )}
            >
              {avatarImageUrl ? (
                <img
                  src={avatarImageUrl}
                  alt={avatarName ?? 'Avatar'}
                  className="size-9 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="size-9 shrink-0 rounded-full bg-foreground-muted/20" />
              )}
              <span className="flex-1 truncate text-left text-sm">
                {avatarName ?? 'Not selected'}
              </span>
              <ChevronRight size={18} className="shrink-0 text-foreground-muted" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-1">
            <ScrollArea className="max-h-[320px]">
              {/* Not selected option */}
              <button
                onClick={() => {
                  onSelectAvatar({ kind: 'none' });
                  setAvatarPopoverOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                  'hover:bg-accent',
                  !selectedLibraryImageId && !avatarName && 'bg-accent'
                )}
              >
                <div className="size-10 shrink-0 rounded-full bg-foreground-muted/20" />
                <span className="text-foreground-muted">Not selected</span>
              </button>

              {/* Available Avatars */}
              {libraryAvatars.map(avatar => (
                <button
                  key={avatar._id}
                  onClick={() => {
                    onSelectAvatar({ kind: 'library-avatar', avatarId: avatar._id });
                    setAvatarPopoverOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                    'hover:bg-accent',
                    selectedLibraryImageId === avatar._id && 'bg-accent'
                  )}
                >
                  {avatar.imageUrl ? (
                    <img
                      src={avatar.imageUrl}
                      alt={avatar.referencePersonName ?? 'Avatar'}
                      className="size-10 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="size-10 shrink-0 rounded-full bg-foreground-muted/20" />
                  )}
                  <span>{avatar.referencePersonName ?? 'Uploaded Avatar'}</span>
                </button>
              ))}

              {libraryAvatars.length === 0 && (
                <div className="px-2.5 py-3 text-center text-sm text-foreground-muted">
                  No avatars available
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      {/* Voice selector row */}
      <div className="mb-4">
        <div className="mb-1.5 text-sm text-foreground-muted">Voice</div>
        <Popover open={voicePopoverOpen} onOpenChange={setVoicePopoverOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg border border-border',
                'bg-background-secondary px-3 py-2.5 transition-colors',
                'hover:border-border-hover hover:bg-background-secondary/80'
              )}
            >
              <span className="flex-1 truncate text-left text-sm">
                {voiceName ?? 'Not selected'}
              </span>
              <ChevronRight size={18} className="shrink-0 text-foreground-muted" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-1">
            <ScrollArea className="max-h-[260px]">
              {voices.map(voice => (
                <button
                  key={voice._id}
                  onClick={() => {
                    onSelectVoice(voice._id);
                    setVoicePopoverOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                    'hover:bg-accent',
                    voiceName === voice.name && 'bg-accent'
                  )}
                >
                  {voice.name}
                </button>
              ))}
              {voices.length === 0 && (
                <div className="px-2.5 py-3 text-center text-sm text-foreground-muted">
                  No voices available
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Render Scene button */}
      <button
        onClick={onRenderScene}
        disabled={isRendering || !canRender}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5',
          'bg-primary text-primary-foreground text-base font-medium',
          'transition-colors hover:bg-primary/90',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
      >
        {isRendering ? <Loader2 size={18} className="animate-spin" /> : <Film size={18} />}
        Render Scene
      </button>
    </div>
  );
}

export { AvatarVoicePanel };
